// Small charts of the analytics surfaces (M7 slice 7): a Sparkline (one series, inline beside a figure) and a LineChart
// (a few named series over the same days, with a legend). SVG only, no library; the series colours come from the tokens and
// never carry meaning alone — the legend names every series and the chart's label states what it shows.
import type { ReactNode } from 'react';

export interface SparklineProps {
  values: number[];
  /** what the line shows, for assistive technology and the tooltip ("Unique users per day: 3, 5, 4") */
  label: string;
  width?: number;
  height?: number;
  /** a second series drawn dashed on the same scale (the segment's "left" beside "entered") */
  second?: number[];
  title?: string;
}

function points(values: number[], width: number, height: number, max: number): string {
  if (values.length === 0) return '';
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  return values.map((v, i) => `${(values.length > 1 ? i * step : width / 2).toFixed(1)},${(height - 1 - (max > 0 ? (v / max) * (height - 2) : 0)).toFixed(1)}`).join(' ');
}

export function Sparkline({ values, label, width = 80, height = 20, second, title }: SparklineProps) {
  const max = Math.max(0, ...values, ...(second ?? []));
  return (
    <svg className="sparkline" role="img" aria-label={label} width={width} height={height} viewBox={`0 0 ${width} ${height}`} data-values={values.join(',')}>
      <title>{title ?? label}</title>
      {second && second.length > 0 && <polyline className="spark-series spark-series-1 spark-dashed" points={points(second, width, height, max)} fill="none" vectorEffect="non-scaling-stroke" />}
      {values.length > 0 && <polyline className="spark-series spark-series-0" points={points(values, width, height, max)} fill="none" vectorEffect="non-scaling-stroke" />}
    </svg>
  );
}

export interface LineSeries {
  name: string;
  values: number[];
}

/** A few series over the same x labels (days), with a legend; the y axis shows its maximum. */
export function LineChart({ series, labels, label, width = 360, height = 120, footer }: { series: LineSeries[]; labels: string[]; label: string; width?: number; height?: number; footer?: ReactNode }) {
  const max = Math.max(0, ...series.flatMap((s) => s.values));
  const pad = 4;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  return (
    <figure className="line-chart" aria-label={label}>
      <svg role="img" aria-label={`${label}: ${series.map((s) => `${s.name} ${s.values.join(', ')}`).join('; ')}`} width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <line className="chart-grid" x1={pad} y1={pad} x2={width - pad} y2={pad} vectorEffect="non-scaling-stroke" />
        <line className="chart-axis" x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} vectorEffect="non-scaling-stroke" />
        {series.map((s, i) => (
          <g key={s.name} transform={`translate(${pad},${pad})`}>
            <polyline className={`spark-series spark-series-${i % 6}`} points={points(s.values, innerW, innerH, max)} fill="none" vectorEffect="non-scaling-stroke" />
          </g>
        ))}
      </svg>
      <figcaption className="chart-legend">
        {series.map((s, i) => (
          <span key={s.name} className="chart-legend-item">
            <span className={`chart-swatch spark-series-${i % 6}`} aria-hidden="true" /> {s.name}
          </span>
        ))}
        <span className="chart-meta">
          <span>max {max}</span>
          {labels.length > 0 && (
            <span>
              {labels[0]} → {labels[labels.length - 1]}
            </span>
          )}
        </span>
        {footer}
      </figcaption>
    </figure>
  );
}
